// API Client - Laravel Backend Integration
// Ersetzt komplett Supabase mit Laravel API

/**
 * Laravel API Client Configuration
 */
const DEBUG = import.meta.env.DEV || String(import.meta.env.VITE_DEBUG).toLowerCase() === 'true';

const normalizeBaseURL = (url) => {
  if (!url) {
    return '';
  }

  return url
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api(?:\.php)?$/, '');
};

const API_CONFIG = {
  baseURL: (() => {
    // Production should use the current origin so the deployed host stays consistent.
    if (window.location.hostname === 'spreewald-lift.de' || 
        window.location.hostname === 'www.spreewald-lift.de' ||
        window.location.origin.includes('spreewald-lift.de')) {
      return normalizeBaseURL(window.location.origin);
    }
    
    // Check for explicit environment variable
    if (import.meta.env.VITE_LARAVEL_API_URL) {
      return normalizeBaseURL(import.meta.env.VITE_LARAVEL_API_URL);
    }
    
    // Local development talks directly to the Laravel server.
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:8000';
    }
    
    return normalizeBaseURL(window.location.origin || 'https://spreewald-lift.de');
  })(),
  timeout: 30000,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
};

if (DEBUG) {
  console.log('API Configuration:', {
    baseURL: API_CONFIG.baseURL,
    envURL: import.meta.env.VITE_LARAVEL_API_URL,
    hostname: window.location.hostname,
    origin: window.location.origin
  });
}

// Test function to verify API connection
window.testAPIConnection = async () => {
  try {
    const testUrl = `${API_CONFIG.baseURL}/api/test`;
    
    if (DEBUG) {
      console.log('Testing API connection:', testUrl);
    }

    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    if (DEBUG) {
      console.log('API test successful:', data);
    }
    return data;
  } catch (error) {
    console.error('API Test failed:', error);
    return null;
  }
};

if (DEBUG) {
  setTimeout(() => {
    window.testAPIConnection();
  }, 1000);
}

/**
 * API Client for Laravel Backend
 */
class LaravelAPIClient {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.token = localStorage.getItem('auth_token');
  }

  normalizeEndpoint(endpoint) {
    return endpoint.replace(/^\/api\.php(?=\/)/, '/api');
  }

  buildURL(endpoint) {
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);

    if (/^https?:\/\//i.test(normalizedEndpoint)) {
      return normalizedEndpoint;
    }

    return `${this.baseURL}${normalizedEndpoint}`;
  }

  /**
   * Set authentication token
   */
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  /**
   * Get authentication headers
   */
  getHeaders() {
    const headers = { ...API_CONFIG.headers };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  /**
   * Handle API responses
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      let errorData = {};
      
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      
      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
        url: response.url
      });
      
      throw new APIError(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );
    }
    return response.json();
  }

  /**
   * Make HTTP request
   */
  async request(endpoint, options = {}) {
    // Check if we have real data stored locally first (from admin interface)
    const localData = this.getLocalData(endpoint);
    if (localData && localData.length > 0) {
      return { data: localData, meta: { total: localData.length, page: 1, limit: 50 } };
    }

    // Try the real API second
    const url = this.buildURL(endpoint);
    
    const config = {
      headers: this.getHeaders(),
      ...options
    };

    if (DEBUG) {
      console.log('Making API request:', {
        originalEndpoint: endpoint,
        url,
        method: config.method || 'GET',
        headers: config.headers
      });
    }

    try {
      const response = await fetch(url, config);
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API returned non-JSON response');
      }
      
      const data = await this.handleResponse(response);
      
      // If we get real API data, prefer it over mock data
      if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
        return data;
      } else {
        throw new Error('Empty API response');
      }
    } catch (error) {
      if (DEBUG) {
        console.warn('Real API failed, falling back to mock data for:', endpoint);
      }
      
      // Fallback to mock data only when real API fails
      const mockResponse = await this.getMockData(endpoint);
      if (mockResponse) {
        return mockResponse;
      }
      
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(`Network error: ${error.message}`, 0, error);
    }
  }

  /**
   * Get mock data for endpoint
   */
  async getMockData(endpoint) {
    // Dynamic import to avoid bundling mock data when not needed
    const { mockContent, mockPlatforms, mockSettings, mockBookings, mockCustomers, mockInvoices } = await import('./mockData.js');
    
    if (endpoint === '/api/test') {
      return {
        message: 'Mock API is working!',
        timestamp: new Date().toISOString(),
        server: 'Mock Server',
        version: '1.0.0-mock'
      };
    }
    
    if (endpoint.startsWith('/api/content')) {
      return {
        data: mockContent,
        meta: {
          total: mockContent.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    if (endpoint.startsWith('/api/platforms')) {
      return {
        data: mockPlatforms,
        meta: {
          total: mockPlatforms.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    if (endpoint.startsWith('/api/settings')) {
      return {
        data: mockSettings,
        meta: {
          total: mockSettings.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    if (endpoint.startsWith('/api/bookings')) {
      return {
        data: mockBookings,
        meta: {
          total: mockBookings.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    if (endpoint.startsWith('/api/customers')) {
      return {
        data: mockCustomers,
        meta: {
          total: mockCustomers.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    if (endpoint.startsWith('/api/invoices')) {
      return {
        data: mockInvoices,
        meta: {
          total: mockInvoices.length,
          page: 1,
          limit: 50
        }
      };
    }
    
    return null;
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    // Add query parameters if any
    if (Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, params[key]);
        }
      });
      const queryString = searchParams.toString();
      endpoint += (endpoint.includes('?') ? '&' : '?') + queryString;
    }
    
    return this.request(endpoint, {
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * Upload file
   */
  async upload(endpoint, file, additionalData = {}) {
    if (DEBUG) {
      console.log('API client upload:', {
        endpoint,
        file,
        additionalData
      });
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional form data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    const url = this.buildURL(endpoint);

    const headers = { ...this.getHeaders() };
    delete headers['Content-Type']; // Let browser set multipart boundary

    if (DEBUG) {
      console.log('Upload URL:', url);
      console.log('Upload headers:', headers);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (DEBUG) {
        console.log('Upload response status:', response.status);
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (DEBUG) {
        console.log('Upload successful:', data);
      }
      return data;
    } catch (error) {
      console.error('Upload error in API client:', error);
      throw error;
    }
  }

  /**
   * Get locally stored real data (from admin interface)
   */
  getLocalData(endpoint) {
    if (endpoint.startsWith('/api/platforms')) {
      const platforms = JSON.parse(localStorage.getItem('admin_platforms') || '[]');
      return platforms;
    }
    
    if (endpoint.startsWith('/api/content')) {
      const content = JSON.parse(localStorage.getItem('admin_content') || '[]');
      return content;
    }
    
    return null;
  }

  /**
   * Store real data locally (called from admin interface)
   */
  storeLocalData(endpoint, data) {
    if (endpoint.startsWith('/api/platforms')) {
      localStorage.setItem('admin_platforms', JSON.stringify(data));
    }
    
    if (endpoint.startsWith('/api/content')) {
      localStorage.setItem('admin_content', JSON.stringify(data));
    }
  }
}

/**
 * Custom error class for API errors
 */
export class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Create API client instance
 */
export const apiClient = new LaravelAPIClient();

/**
 * Helper function to handle API responses with error handling
 */
export const handleAPIResponse = async (apiCall) => {
  try {
    return { data: await apiCall, error: null };
  } catch (error) {
    console.error('API Error:', error);
    return { data: null, error };
  }
};

/**
 * Authentication helpers
 */
export const auth = {
  async login(email, password) {
    const response = await apiClient.post('/api/auth/login', { email, password });
    if (response.token) {
      apiClient.setToken(response.token);
    }
    return response;
  },

  async register(userData) {
    const response = await apiClient.post('/api/auth/register', userData);
    if (response.token) {
      apiClient.setToken(response.token);
    }
    return response;
  },

  async logout() {
    try {
      await apiClient.post('/api/auth/logout');
    } finally {
      apiClient.setToken(null);
    }
  },

  async refreshToken() {
    const response = await apiClient.post('/api/auth/refresh');
    if (response.token) {
      apiClient.setToken(response.token);
    }
    return response;
  },

  async getUser() {
    return apiClient.get('/api/auth/user');
  },

  isAuthenticated() {
    return !!apiClient.token;
  }
};

export default apiClient; 
